import React from 'react';
import {
  View,
  Text,
  Image,
  Pressable,
  ScrollView,
  Platform,
  StyleSheet,
} from 'react-native';
import {SafeAreaView} from 'react-native-safe-area-context';
import {Routes} from '../../navigation/Routes';
import logo from '../../image/logo.png';

const FEATURES = [
  {
    icon: '🍽️',
    title: 'Food',
    description:
      'Discover local food options and connect with nearby providers for quick, convenient service.',
  },
  {
    icon: '🧹',
    title: 'HouseHold Helps',
    description:
      'Find help for everyday tasks—cleaning, small chores, and assistance around the home.',
  },
  {
    icon: '🚗',
    title: 'Transportations',
    description:
      'Connect for local transport needs—rides, deliveries, and getting from A to B with ease.',
  },
];

const HIGHLIGHTS = [
  {
    title: 'Local-first experience',
    description: 'Connect to services available in your area.',
  },
  {
    title: 'Clear categories',
    description: 'Food, HouseHold Helps, and Transportations—easy to browse.',
  },
  {
    title: 'Customer-friendly design',
    description: 'Clean layout, readable text, and mobile-ready styling.',
  },
];

export default function LandingScreen({navigation}) {
  return (
    <SafeAreaView style={styles.safe} edges={['top', 'left', 'right']}>
      <View style={styles.header}>
        <View style={styles.brand}>
          <Image source={logo} style={styles.logo} />
          <View>
            <Text style={styles.brandTitle}>Eabu</Text>
            <Text style={styles.brandSubtitle}>Food and Beyond - Locally</Text>
          </View>
        </View>

        <Pressable
          onPress={() => navigation.navigate(Routes.SignUpScreen)}
          accessibilityRole="button"
          accessibilityLabel="Login or Register"
          hitSlop={{top: 10, bottom: 10, left: 10, right: 10}}
          style={({pressed}) => [
            styles.loginPill,
            pressed && styles.loginPillPressed,
          ]}>
          <Text style={styles.loginPillText}>Login / Register</Text>
        </Pressable>
      </View>

      <ScrollView contentContainerStyle={styles.scroll}>
        <View style={styles.heroCard}>
          <Text style={styles.headline}>
            Connect locally for everyday services.
          </Text>
          <Text style={styles.subhead}>
            This app allows users to connect locally for the services in{' '}
            <Text style={styles.bold}>Food</Text>,{' '}
            <Text style={styles.bold}>HouseHold Helps</Text> and{' '}
            <Text style={styles.bold}>Transportations</Text>.
          </Text>

          <View style={styles.grid}>
            {FEATURES.map(feature => (
              <View style={styles.feature} key={feature.title}>
                <View style={styles.iconWrap}>
                  <Text style={styles.icon}>{feature.icon}</Text>
                </View>
                <Text style={styles.featureTitle}>{feature.title}</Text>
                <Text style={styles.featureText}>{feature.description}</Text>
              </View>
            ))}
          </View>
        </View>

        <View style={styles.asideCard}>
          <Text style={styles.asideTitle}>How it works</Text>
          <Text style={styles.asideText}>
            Choose a service category, share your location, and connect with
            nearby providers. Designed to be simple, fast, and
            customer-friendly.
          </Text>

          <View style={styles.list}>
            {HIGHLIGHTS.map(item => (
              <View style={styles.listItem} key={item.title}>
                <View style={styles.dot} />
                <View style={styles.flexShrink}>
                  <Text style={styles.listItemTitle}>{item.title}</Text>
                  <Text style={styles.listItemText}>{item.description}</Text>
                </View>
              </View>
            ))}
          </View>
        </View>

        <View style={styles.footer}>
          <Text style={styles.footerText}>
            © {new Date().getFullYear()} Local Services Connector
          </Text>
          <Text style={styles.footerText}>
            Built for a smooth customer experience
          </Text>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}

const HEADER_BG = Platform.select({
  ios: '#0A84FF',
  android: '#0EA5E9',
  default: '#0EA5E9',
});

const styles = StyleSheet.create({
  safe: {
    flex: 1,
    backgroundColor: '#F2F2F7',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: HEADER_BG,
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  brand: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    flexShrink: 1,
  },
  logo: {
    width: 36,
    height: 36,
    borderRadius: 10,
  },
  brandTitle: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '800',
  },
  brandSubtitle: {
    color: 'rgba(255,255,255,0.85)',
    fontSize: 11,
    marginTop: 1,
  },
  loginPill: {
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.6)',
    borderRadius: 999,
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  loginPillPressed: {
    backgroundColor: 'rgba(255,255,255,0.15)',
  },
  loginPillText: {
    color: '#FFFFFF',
    fontSize: 13,
    fontWeight: '700',
  },
  scroll: {
    padding: 16,
    paddingBottom: 32,
    gap: 14,
  },
  heroCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 18,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: '#E5E7EB',
    shadowColor: '#000',
    shadowOpacity: 0.06,
    shadowRadius: 8,
    shadowOffset: {width: 0, height: 3},
    elevation: 2,
  },
  headline: {
    fontSize: 24,
    fontWeight: '800',
    color: '#111827',
    letterSpacing: -0.3,
  },
  subhead: {
    marginTop: 10,
    fontSize: 14.5,
    lineHeight: 21,
    color: '#4B5563',
  },
  bold: {
    fontWeight: '700',
    color: '#111827',
  },
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    marginTop: 18,
  },
  feature: {
    flexGrow: 1,
    flexBasis: 220,
    minWidth: 200,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    backgroundColor: '#F9FAFB',
    padding: 14,
  },
  iconWrap: {
    width: 38,
    height: 38,
    borderRadius: 12,
    backgroundColor: 'rgba(14,165,233,0.12)',
    borderWidth: 1,
    borderColor: 'rgba(14,165,233,0.35)',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 10,
  },
  icon: {
    fontSize: 18,
  },
  featureTitle: {
    fontSize: 15,
    fontWeight: '700',
    color: '#111827',
  },
  featureText: {
    marginTop: 6,
    fontSize: 12.8,
    lineHeight: 18,
    color: '#6B7280',
  },
  asideCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 18,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: '#E5E7EB',
    shadowColor: '#000',
    shadowOpacity: 0.06,
    shadowRadius: 8,
    shadowOffset: {width: 0, height: 3},
    elevation: 2,
  },
  asideTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: '#111827',
  },
  asideText: {
    marginTop: 8,
    fontSize: 13.5,
    lineHeight: 20,
    color: '#4B5563',
  },
  list: {
    marginTop: 14,
    gap: 10,
  },
  listItem: {
    flexDirection: 'row',
    gap: 10,
    alignItems: 'flex-start',
    padding: 12,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    backgroundColor: '#F9FAFB',
  },
  flexShrink: {
    flexShrink: 1,
  },
  dot: {
    width: 9,
    height: 9,
    borderRadius: 99,
    marginTop: 5,
    backgroundColor: HEADER_BG,
  },
  listItemTitle: {
    fontSize: 13.5,
    fontWeight: '700',
    color: '#111827',
  },
  listItemText: {
    marginTop: 2,
    fontSize: 12.5,
    color: '#6B7280',
  },
  footer: {
    marginTop: 4,
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    gap: 6,
    paddingHorizontal: 4,
  },
  footerText: {
    fontSize: 12,
    color: '#6B7280',
  },
});
